import React, { useState } from 'react';
import { Star, Send, User, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { reviewsApi } from '../api/client';
import { useToast } from '../hooks/use-toast';

export default function ReviewsSection({ firm, initialReviews = [], onNewReview }) {
  const [reviews, setReviews] = useState(initialReviews);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!name || rating === 0 || comment.length < 5) {
      toast({ title: 'Preencha todos os campos', description: 'Nome, nota e comentário de pelo menos 5 caracteres.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await reviewsApi.create(firm.id, { authorName: name, rating, comment });
      setReviews([r, ...reviews]);
      setName(''); setRating(0); setComment('');
      toast({ title: 'Avaliação enviada!', description: 'Obrigado pelo seu depoimento.' });
      onNewReview && onNewReview(r);
    } catch (err) {
      toast({ title: 'Erro ao enviar', description: err.response?.data?.detail || 'Tente novamente.', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6">
      {/* Form */}
      <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6">
        <h2 className="text-lg font-semibold text-[#153e44] mb-1">Deixe sua avaliação</h2>
        <p className="text-sm text-[#425a62] mb-4">Ajude outras pessoas contando sua experiência com {firm.name}.</p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Seu nome</label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: João S." className="pl-9" maxLength={80} />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Nota</label>
              <div className="flex items-center gap-1 h-10" onMouseLeave={() => setHover(0)}>
                {[1,2,3,4,5].map(i => (
                  <button key={i} type="button" onMouseEnter={() => setHover(i)} onClick={() => setRating(i)} className="p-0.5" aria-label={`${i} estrelas`}>
                    <Star className={`w-7 h-7 transition-colors ${(hover || rating) >= i ? 'fill-[#153e44] text-[#153e44]' : 'text-[#6b858e]'}`} />
                  </button>
                ))}
                {rating > 0 && <span className="ml-2 text-sm font-semibold text-[#153e44]">{rating}/5</span>}
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-1.5 block">Seu depoimento</label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder="Conte como foi o atendimento, o resultado obtido..." maxLength={1200} />
            <div className="text-xs text-[#6b858e] text-right mt-1">{comment.length}/1200</div>
          </div>
          <Button type="submit" disabled={submitting} className="bg-[#153e44] hover:bg-[#0c3036]">
            <Send className="w-4 h-4 mr-2" /> {submitting ? 'Enviando...' : 'Enviar avaliação'}
          </Button>
        </form>
      </section>

      {/* List */}
      <section className="bg-white rounded-[10px] border border-[#cbd8dc] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#153e44]">Avaliações ({reviews.length})</h2>
          {firm.rating > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`w-4 h-4 ${i <= Math.round(firm.rating) ? 'fill-[#153e44] text-[#153e44]' : 'text-[#6b858e]'}`} />
                ))}
              </div>
              <span className="font-semibold text-[#153e44]">{firm.rating.toFixed(1)}</span>
            </div>
          )}
        </div>
        {firm.ratingSource && (
          <div className="mb-4 aj-edition bg-[#f7fafb] rounded-r-[7px] py-3 px-5 text-sm text-[#354c54]" data-testid="rating-source-note">
            Nota <strong className="text-[#122f36]">{firm.rating.toFixed(1)}</strong> com <strong className="text-[#122f36]">{firm.reviews}</strong> avaliações no perfil do {firm.ratingSource.platform} (consultado em {new Date(firm.ratingSource.collectedAt).toLocaleDateString('pt-BR')}).{' '}
            {firm.ratingSource.url && <a href={firm.ratingSource.url} target="_blank" rel="noopener noreferrer" className="aj-link font-semibold">Ver avaliações no {firm.ratingSource.platform} →</a>}
          </div>
        )}
        {reviews.length === 0 ? (
          <p className="text-sm text-[#52676e] italic">{firm.ratingSource ? 'As avaliações individuais não foram importadas. Você pode deixar a sua aqui.' : 'Este escritório ainda não recebeu avaliações. Seja o primeiro!'}</p>
        ) : (
          <div className="space-y-4">
            {reviews.map(r => (
              <div key={r.id} className="border-b border-[#e1e9eb] last:border-0 pb-4 last:pb-0">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-[#153e44] text-white font-semibold flex items-center justify-center text-sm">
                      {r.authorName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-[#153e44] text-sm flex items-center gap-1.5">
                        {r.authorName}
                        {r.verified && <Shield className="w-3.5 h-3.5 text-[#0a7d3e]" title="Verificado" />}
                        {r.source === 'google' && <span className="text-[10px] font-medium bg-[#f2f6f7] text-[#425a62] px-1.5 py-0.5 rounded" data-testid="review-source-google">Publicado no Google</span>}
                      </div>
                      <div className="text-xs text-[#52676e]">{r.imported ? 'Importada em ' : ''}{r.createdAt ? new Date(r.createdAt).toLocaleDateString('pt-BR') : ''}</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-4 h-4 ${i <= r.rating ? 'fill-[#153e44] text-[#153e44]' : 'text-[#6b858e]'}`} />
                    ))}
                  </div>
                </div>
                <p className="text-[#354c54] text-sm leading-relaxed mt-2">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
